<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import type { InternalRuleItem } from "async-validator/dist-types/interface";
import { StatusCodes } from "http-status-codes";
import { storeToRefs } from "pinia";
import { onMounted, ref, type Ref } from "vue";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import LxcError from "~/core/models/LxcError.class";
import type { TreeNodes } from "~/core/models/TreeNodes.class";
import {
  NotificationKey,
  showNotificationError,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP } from "~/modules/applicationManagement/constants";
import type { CreateInterfaceRequestPayload } from "~/modules/applicationManagement/models/CreateInterfaceRequestPayload.interface";
import type { CreateInterfaceResponse } from "~/modules/applicationManagement/models/CreateInterfaceResponse.interface";
import { InterfaceAuthenticationType } from "~/modules/applicationManagement/models/InterfaceAuthenticationType.enum";
import type { UpdateInterfaceRequestPayload } from "~/modules/applicationManagement/models/UpdateInterfaceRequestPayload.interface";
import applicationMgtService from "~/modules/applicationManagement/services/applicationMgt.service";
import { canManageThirdPartyApps } from "~/modules/applicationManagement/utils/canManageThirdPartyApps";
import { canViewThirdPartyApps } from "~/modules/applicationManagement/utils/canViewThirdPartyApps";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { mapSectorsToTreeNodes } from "~/modules/sector/utils/mapSectorsToTreeNodes";
import { useFetchProfiles } from "~/modules/user/composables/useFetchProfiles";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";
import ILxcInfo from "~icons/lxc/info";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  selectedInterfaceClientId?: string | null;
}>();
const emit = defineEmits(["close", "created"]);

const show = ref(false);

const { t } = useI18n();
const { userSession } = useUserSession();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { results: profiles, fetchData: fetchAllProfiles } = useFetchProfiles({
  organizationCode: currentOrganization.value?.code,
});
const { sectors } = storeToRefs(useSectorStore());

const route = useRoute();
const serialize = useSerialize();

const showModalSuccess: Ref<boolean> = ref(false);
const newInterfaceResponse: Ref<CreateInterfaceResponse | undefined> = ref();
const error: Ref<LxcError | undefined | null> = ref();
const isLoading: Ref<boolean> = ref(false);
const sideCanvasRef: Ref<typeof LxcSideCanvas | undefined> = ref();

const lxcTreeNodes = ref<TreeNodes[]>();
const treeSelectKey = ref(0);

const isInterfaceUpdate: ComputedRef<boolean> = computed(() => {
  return (
    route.path.startsWith(PATH_PARAMETERS_APPLICATIONS_THIRD_PART_APP) &&
    !!route.params.clientId
  );
});
const sidePanelHeader: ComputedRef<string> = computed(() => {
  return isInterfaceUpdate.value
    ? t("applicationMgt.updateInterface")
    : t("applicationMgt.newInterface");
});
const closeButtonLabel: ComputedRef<string> = computed(() => {
  return isInterfaceUpdate.value ? t("button.cancel") : t("button.close");
});
const validateButtonLabel: ComputedRef<string> = computed(() => {
  return isInterfaceUpdate.value
    ? t("button.validate")
    : t("applicationMgt.generateNewInterface");
});
const confirmTitle: ComputedRef<string> = computed(() => {
  return isInterfaceUpdate.value
    ? t("applicationMgt.tabs.thirdPartyApp.form.confirmCancelUpdate.title")
    : t("applicationMgt.tabs.thirdPartyApp.form.confirmCancelCreate.title");
});
const confirmMessage: ComputedRef<string> = computed(() => {
  return isInterfaceUpdate.value
    ? t("applicationMgt.tabs.thirdPartyApp.form.confirmCancelUpdate.message")
    : t("applicationMgt.tabs.thirdPartyApp.form.confirmCancelCreate.message");
});

interface InterfacePayloadI {
  organizationName?: string;
  name?: string;
  profile?: string;
  sectors?: string[];
  authenticationType?: InterfaceAuthenticationType;
  clientId?: string;
}

const interfaceFormRef: Ref<HTMLFormElement | undefined | null> = ref();
const interfaceForm = reactive<InterfacePayloadI>({
  organizationName: currentOrganization.value?.name,
  name: "",
  profile: undefined,
  sectors: undefined,
  authenticationType: InterfaceAuthenticationType.CLIENTGRANT,
  clientId: undefined,
});

let initInterfaceFormStringified: string = serialize(interfaceForm);
const edited: ComputedRef<boolean> = computed(
  () => serialize(interfaceForm) !== initInterfaceFormStringified,
);
const isSaving: Ref<boolean> = ref(false);

async function getInterfaceDetailsByClientId(clientId: string) {
  isLoading.value = true;
  const response = await applicationMgtService.getAppInterfaceByClientId(
    clientId,
    currentOrganization.value?.code,
  );
  if (LxcError.check(response)) {
    error.value = response;
  } else {
    Object.assign(interfaceForm, {
      name: response.name,
      profile: response.profileCode.toString(),
      sectors: response.sectorCodes.map((sectorCode) => sectorCode.toString()),
      clientId: response.clientId,
    });

    initInterfaceFormStringified = serialize(interfaceForm);
  }
  isLoading.value = false;
}

async function getInterfaceDetails() {
  if (props.selectedInterfaceClientId) {
    open();
    await getInterfaceDetailsByClientId(props.selectedInterfaceClientId);
  } else if (props.selectedInterfaceClientId === null) {
    open();
  } else {
    close();
  }
  treeSelectKey.value += 1;
  initInterfaceFormStringified = serialize(interfaceForm);
}

const isSelectedSectors = (
  _: InternalRuleItem,
  value: string[],
  callback: (error?: Error) => void,
) => {
  return value && value.length > 0
    ? callback()
    : callback(
        new Error(
          t("applicationMgt.tabs.thirdPartyApp.form.validation.sectors"),
        ),
      );
};

const isInterfaceNameValid = (
  _: InternalRuleItem,
  value: string,
  callback: (error?: Error) => void,
) => {
  const validator = /^[a-zA-Z0-9_]+$/;
  if (!validator.test(value)) {
    return callback(
      new Error(
        t("applicationMgt.tabs.thirdPartyApp.form.validation.nameFormat"),
      ),
    );
  }
  return callback();
};

const interfaceFormRules = {
  organizationName: [
    {
      required: true,
      message: t(
        "applicationMgt.tabs.thirdPartyApp.form.validation.organizationName",
      ),
      trigger: "change",
    },
  ],
  name: [
    {
      required: true,
      message: t("applicationMgt.tabs.thirdPartyApp.form.validation.name"),
      whitespace: true,
      trigger: "blur",
    },
    {
      max: 50,
      message: t("input.error.maxLength", { maxLength: 50 }),
      whitespace: true,
      trigger: "blur",
    },
    { validator: isInterfaceNameValid, trigger: "blur" },
  ],
  profile: [
    {
      required: true,
      message: t("applicationMgt.tabs.thirdPartyApp.form.validation.profile"),
      trigger: "change",
    },
  ],
  sectors: [
    {
      required: true,
      validator: isSelectedSectors,
      trigger: "update:model-value",
    },
  ],
  authenticationType: [
    {
      required: true,
      message: t(
        "applicationMgt.tabs.thirdPartyApp.form.validation.authenticationType",
      ),
      trigger: "change",
    },
  ],
};

async function isFormValid() {
  return await interfaceFormRef.value?.validate().catch(() => false);
}

async function onSubmit() {
  if (
    (await isFormValid()) &&
    userSession &&
    currentOrganization.value &&
    canManageThirdPartyApps()
  ) {
    isSaving.value = true;
    if (isInterfaceUpdate && props.selectedInterfaceClientId) {
      const updateInterfacePayload: UpdateInterfaceRequestPayload = {
        profileCode: interfaceForm.profile ?? "",
        sectorCodes: interfaceForm.sectors ?? [],
      };

      // Update the interface
      const response = await applicationMgtService.updateAppInterface(
        props.selectedInterfaceClientId,
        updateInterfacePayload,
        currentOrganization.value.code,
      );
      if (LxcError.check(response)) {
        response.notify(NotificationKey.saveError);
      } else {
        showNotificationSuccess(t(NotificationKey.saveSuccess));
        close({ edited: true });
      }
    } else {
      const newInterface: CreateInterfaceRequestPayload = {
        authenticationType:
          interfaceForm.authenticationType ??
          InterfaceAuthenticationType.CLIENTGRANT,
        organizationCode: currentOrganization.value.code,
        organizationName:
          interfaceForm.organizationName ??
          currentOrganization.value?.name ??
          "",
        name: interfaceForm.name ?? "",
        profileCode: interfaceForm.profile ?? "",
        sectorCodes: interfaceForm.sectors ?? [],
      };

      // Create a new interface
      const response =
        await applicationMgtService.createAppInterface(newInterface);
      if (LxcError.check(response)) {
        switch ((response as LxcError).status) {
          case StatusCodes.CONFLICT:
            showNotificationError(
              t(NotificationKey.saveError),
              t("applicationMgt.tabs.thirdPartyApp.form.validation.uniqueName"),
            );
            break;
          case StatusCodes.PAYMENT_REQUIRED:
            showNotificationError(
              t(NotificationKey.saveError),
              t(
                "applicationMgt.tabs.thirdPartyApp.licenseMessage.disableNewInterface",
              ),
            );
            break;
          default:
            (response as LxcError).notify(NotificationKey.saveError);
            break;
        }
      } else {
        showNotificationSuccess(t(NotificationKey.saveSuccess));
        showModalSuccess.value = true;
        newInterfaceResponse.value = response;
        emit("created");
        close({ edited: true });
      }
    }
    isSaving.value = false;
  }
}

function open() {
  show.value = true;
}

function close(options?: CloseEvent) {
  show.value = false;

  emit("close", options);

  Object.assign(interfaceForm, {
    name: "",
    profile: undefined,
    sectors: undefined,
    clientId: undefined,
  });

  initInterfaceFormStringified = serialize(interfaceForm);

  setTimeout(interfaceFormRef.value?.clearValidate, 0);

  error.value = undefined;
}

function onCloseResponseModal() {
  showModalSuccess.value = false;
}

function onCheckedSectorsChanged(sectorCodes: string[]) {
  interfaceForm.sectors = sectorCodes ?? [];
}

const onClickCancelButton = () => {
  sideCanvasRef.value?.hide();
};

watch(() => props.selectedInterfaceClientId, getInterfaceDetails);

onMounted(async () => {
  await fetchAllProfiles();

  if (sectors.value) {
    lxcTreeNodes.value = mapSectorsToTreeNodes(sectors.value);
  }
});

const radioButtonWrapperClass = [
  "py-3",
  "rounded-xl",
  "px-4",
  "bg-white",
  "border",
  "border-gray-300",
  "w-36",
];
</script>

<template>
  <lxc-side-canvas
    v-if="canViewThirdPartyApps()"
    ref="sideCanvasRef"
    v-model:show="show"
    size="1/3"
    :header="sidePanelHeader"
    :close-tooltip="t('filters.close')"
    :confirm-enabled="edited"
    :confirm-title="confirmTitle"
    :confirm-message="confirmMessage"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    @discard="close"
    @hidden="close"
  >
    <container-component :px="0" :py="0" :error="error" :is-loading="isLoading">
      <h6 class="mb-4">
        {{ t("applicationMgt.tabs.thirdPartyApp.form.title") }}
      </h6>
      <lxc-form
        ref="interfaceFormRef"
        :model="interfaceForm"
        :rules="interfaceFormRules"
        class="flex flex-col gap-2"
      >
        <lxc-form-item
          :label="t('applicationMgt.tabs.thirdPartyApp.form.name')"
          :disabled="isInterfaceUpdate"
          prop="name"
        >
          <lxc-input v-model="interfaceForm.name" type="text" />
        </lxc-form-item>
        <lxc-form-item
          :label="t('applicationMgt.tabs.thirdPartyApp.form.profile.label')"
          prop="profile"
        >
          <lxc-select
            v-model="interfaceForm.profile"
            :disabled="!canManageThirdPartyApps()"
            :placeholder="
              t('applicationMgt.tabs.thirdPartyApp.form.profile.placeholder')
            "
          >
            <lxc-option
              v-for="profile in profiles"
              :key="profile.id"
              :value="`${profile.id}`"
              :label="getProfileLabel(profile)"
            />
          </lxc-select>
        </lxc-form-item>
        <lxc-form-item
          :label="t('applicationMgt.tabs.thirdPartyApp.form.sectors.label')"
          display-label
          prop="sectors"
        >
          <tree-select
            v-if="lxcTreeNodes"
            :key="treeSelectKey"
            :tree="lxcTreeNodes"
            :allow-empty-selection="true"
            :default-checked-values="interfaceForm.sectors"
            :disabled="!canManageThirdPartyApps()"
            :placeholder="
              t('applicationMgt.tabs.thirdPartyApp.form.sectors.placeholder')
            "
            placement="left-start"
            force-inheritance
            @on-checked-node-value-changed="onCheckedSectorsChanged"
          />
        </lxc-form-item>
        <lxc-form-item
          prop="authenticationType"
          :label="
            t('applicationMgt.tabs.thirdPartyApp.form.authenticationType.label')
          "
          display-label
        >
          <div class="flex gap-4">
            <div :class="radioButtonWrapperClass">
              <lxc-radio
                v-model="interfaceForm.authenticationType"
                :value="InterfaceAuthenticationType.CLIENTGRANT"
                :label="
                  t(
                    'applicationMgt.tabs.thirdPartyApp.form.authenticationType.clientGrant',
                  )
                "
                :disabled="isInterfaceUpdate"
              />
            </div>
            <div :class="radioButtonWrapperClass">
              <lxc-radio
                v-model="interfaceForm.authenticationType"
                :value="InterfaceAuthenticationType.MTLS"
                :label="
                  t(
                    'applicationMgt.tabs.thirdPartyApp.form.authenticationType.mtls',
                  )
                "
                disabled
              />
            </div>
          </div>
        </lxc-form-item>
      </lxc-form>
      <div v-if="isInterfaceUpdate" class="flex flex-col gap-6 mt-2">
        <div class="flex gap-4">
          <div class="flex-1">
            <lxc-input
              type="text"
              disabled
              :label="
                t(
                  'applicationMgt.tabs.thirdPartyApp.creationModalSuccess.clientId.label',
                )
              "
              :model-value="interfaceForm?.clientId"
            />
          </div>
          <lxc-copy-to-clipboard
            :title="
              t(
                'applicationMgt.tabs.thirdPartyApp.creationModalSuccess.copyToClipboard.buttonTitle',
              )
            "
            :info-to-copy="interfaceForm?.clientId ?? ''"
          />
        </div>
        <div class="flex gap-2">
          <lxc-info-sign>
            <i-lxc-info
              width="1.25rem"
              height="1.25rem"
              view-box="0 0 1.25rem 1.25rem"
            />
          </lxc-info-sign>
          <div>
            <h6 class="my-0">
              {{
                t(
                  "applicationMgt.tabs.thirdPartyApp.form.update.information.title",
                )
              }}
            </h6>
            <p class="my-1">
              {{
                t(
                  "applicationMgt.tabs.thirdPartyApp.form.update.information.content",
                )
              }}
            </p>
          </div>
        </div>
      </div>
    </container-component>
    <template #footer>
      <div class="flex gap-4">
        <lxc-button
          html-type="button"
          type="tertiary"
          :title="closeButtonLabel"
          @click="onClickCancelButton"
        >
          {{ closeButtonLabel }}
        </lxc-button>
        <lxc-button
          html-type="button"
          type="primary"
          :icon="isInterfaceUpdate ? undefined : ILxcPlus"
          :title="validateButtonLabel"
          class="flex-1"
          :disabled="!edited || !canManageThirdPartyApps() || isSaving"
          :is-loading="isSaving"
          @click="onSubmit"
        >
          {{ validateButtonLabel }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>
  <third-party-application-creation-response-modal
    :show="showModalSuccess"
    :new-interface-response="newInterfaceResponse"
    @close="onCloseResponseModal"
  />
</template>
